import * as React from 'react';
import { connect } from 'react-redux';

import * as classnames from 'classnames';
import Select from 'react-select';
import Option from '../../reactSelect/Option';
import { SingleValue } from '../../reactSelect/CheckboxMultiSelectPipeOption';
import { IApplicationState } from '../../../../reducers';
import {
    PanelType,
    Project,
    ProjectLanguage,
    QTYPES,
    QuestionID,
    QuestionSummary
} from '@nexxt/common/types';
import {
    flattenQuestionSummary,
    getQuestionSummariesWithBuiltIn
} from '../../../../utils/question';

interface IncaVariableSelectProps {
    questionSummaries: QuestionSummary[];
    currentQuestionId: QuestionID;
    currentProject: Project;
    languages: ProjectLanguage[];
    includeCurrentQuestion: boolean;
    availableQTypes: QTYPES[];
    selectedQuestion?: QuestionSummary;
    selectedQuestionValue?: string;
    disabled: boolean;
    handleQuestionSelect: (question) => void;
}

const IncaVariableSelect: React.FC<IncaVariableSelectProps> = (
    props: IncaVariableSelectProps
) => {
    const [questions, setQuestions] = React.useState([]);

    React.useEffect(() => {
        const flattenedQuestionSummaries = flattenQuestionSummary(
            props.questionSummaries
        );

        const filteredQuestionSummaries = filterQuestionSummaries(
            flattenedQuestionSummaries
        );
        const flattenedFilteredQuestionSummaries =
            filteredQuestionSummaries.reduce((arr, q) => {
                if (q.type === QTYPES.SYSTEM_METADATA) {
                    arr.push(...q['options']);
                } else {
                    arr.push(q);
                }
                return arr;
            }, []);
        setQuestions(filteredQuestionSummaries);

        if (props.selectedQuestionValue) {
            const selectedQuestion = flattenedFilteredQuestionSummaries.find(
                (q) => q.value == props.selectedQuestionValue
            );
            props.handleQuestionSelect(selectedQuestion);
        }
    }, []);

    const filterQuestionSummaries = (questionSummaries: QuestionSummary[]) => {
        return questionSummaries
            .filter((questionSummary) => {
                if (
                    !props.includeCurrentQuestion &&
                    questionSummary.id === props.currentQuestionId
                ) {
                    return false;
                }
                return true;
            })
            .filter((questionSummary) =>
                props.availableQTypes.includes(questionSummary.type)
            );
    };

    return (
        <Select
            className={'react-select react-select--multielement'}
            classNamePrefix="react-select"
            components={{ Option, SingleValue }}
            hideSelectedOptions={false}
            options={questions}
            value={props.selectedQuestion}
            onChange={props.handleQuestionSelect}
            isDisabled={props.disabled}
        />
    );
};

const mapStateToProps = (store: IApplicationState) => {
    const {
        questionReducer: { questionSummaries, currentQuestionId },
        projectReducer: { currentProject }
    } = store;
    const { id, languages } = currentProject;
    const showLanguageQuestion = languages?.length > 1;
    const customPanelEnabled =
        currentProject?.panelSettings?.panelType !== PanelType.INCA;
    const questionSummariesWithBuiltIn = getQuestionSummariesWithBuiltIn(
        questionSummaries[id],
        {
            language: showLanguageQuestion,
            panel: customPanelEnabled,
            device: true,
            url: true
        }
    );
    return {
        questionSummaries: questionSummariesWithBuiltIn,
        currentProject,
        currentQuestionId,
        languages
    };
};

/**
 * This component provides a dropdown UI for researcher to select an IncaVariable.
 */
export default connect(mapStateToProps, null)(IncaVariableSelect);
